import { Title } from '@solidjs/meta'
import { DateTime } from 'luxon'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import DateInput from '~/components/DateInput'
import Icon from '~/components/Icon'

const DateInputPage: Component = () => {
  const [selectedDate] = createSignal<string | undefined>(DateTime.now().toISODate())
  const [selectedTime, setSelectedTime] = createSignal<string | undefined>('14:30')

  return (
    <>
      <Title>Date Input - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Date Input</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Gestión de tiempo y calendario. El componente Date Input facilita la captura de fechas, horas o rangos temporales con una interfaz moderna basada en los estándares del sistema.
          </p>
        </div>

        <div class="space-y-12">
          {/* Modos de Selección */}
          <Card>
            <Card.Header
              title="Modos de Captura"
              subtitle="Optimiza el selector para fechas exactas, horas específicas o periodos anuales."
              icon={<Icon icon="material-symbols-light:calendar-month-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-10 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-6">
                  <DateInput mode="date" label="Fecha de Nacimiento" placeholder="DD/MM/AAAA" helperText="Formato estándar para fechas históricas." />
                  <DateInput mode="datetime" label="Cita Médica" placeholder="Fecha y hora" value={DateTime.now().toISO()?.slice(0, 16)} />
                </div>

                <div class="space-y-6">
                  <DateInput mode="time" label="Hora de Alarma" value={selectedTime()} onChange={setSelectedTime} />
                  <div class="grid grid-cols-2 gap-4">
                    <DateInput mode="year" label="Año Escolar" />
                    <DateInput mode="year-month" label="Mes de Factura" />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Restricciones y Validación */}
          <Card>
            <Card.Header
              title="Límites y Restricciones"
              subtitle="Configura rangos permitidos y deshabilita días específicos como fines de semana."
              icon={<Icon icon="material-symbols-light:warning-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="space-y-6">
                  <h4 class="font-bold text-sm text-foreground-subtle">Rango de Selección (Min/Max)</h4>
                  <div class="p-8 bg-surface rounded-xl border border-border shadow-sm space-y-6">
                    <DateInput
                      mode="date"
                      label="Periodo Vacacional (Próximo Mes)"
                      min={DateTime.now().toISODate()}
                      max={DateTime.now().plus({ months: 1 }).toISODate()}
                      helperText="Limitado a los próximos 30 días."
                    />
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="font-bold text-sm text-foreground-subtle">Exclusión Semanal</h4>
                  <div class="p-8 bg-surface rounded-xl border border-border shadow-sm space-y-6">
                    <DateInput
                      mode="date"
                      label="Días Laborables Solo"
                      disabledDates={{ days: [6, 7] }} // Sábado (6) y Domingo (7)
                      helperText="Fines de semana no seleccionables."
                    />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Ejemplo Real: Reservas */}
          <Card>
            <Card.Header
              title="Escenario: Sistema de Reservas"
              subtitle="Integración de múltiples selectores con lógica de dependencia temporal."
              icon={<Icon icon="material-symbols-light:work-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-3xl mx-auto p-10 bg-slate-900 rounded-3xl overflow-hidden relative border border-slate-800">
                <div class="absolute top-0 right-0 p-10 opacity-5 pointer-events-none rotate-12">
                  <Icon icon="material-symbols-light:schedule-outline" size={200} class="text-white" />
                </div>
                <div class="relative z-10 space-y-8">
                  <div class="border-b border-slate-800 pb-6 text-center md:text-left">
                    <h3 class="text-2xl font-black text-white italic tracking-tighter uppercase">Check-In / Check-Out</h3>
                    <p class="text-foreground-subtle text-sm">Gestiona la disponibilidad en tiempo real.</p>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <DateInput mode="date" label="Ingreso" value={selectedDate()} />
                    <DateInput mode="date" label="Salida" min={DateTime.now().plus({ days: 1 }).toISODate()} />
                  </div>
                  <div class="pt-4 border-t border-slate-800 flex flex-wrap justify-between items-center gap-4">
                    <Badge color="info" variant="soft" class="px-4 py-1">
                      Estancia sugerida: 3 noches
                    </Badge>
                    <Button color="primary" variant="contained" icon="material-symbols-light:check-circle-outline">
                      Confirmar Disponibilidad
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Cinco densidades alineadas con Select y Button: xs, sm, md, lg y xl."
              icon={<Icon icon="material-symbols-light:straighten" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4 p-10 bg-surface-muted rounded-2xl border border-border">
                <DateInput mode="date" size="xs" label="Extra Small" placeholder="xs" />
                <DateInput mode="date" size="sm" label="Small" placeholder="sm" />
                <DateInput mode="date" size="md" label="Medium" placeholder="md" />
                <DateInput mode="date" size="lg" label="Large" placeholder="lg" />
                <DateInput mode="date" size="xl" label="Extra Large" placeholder="xl" />
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Manual técnico de las propiedades de DateInput." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">mode</td>
                      <td class="px-6 py-4 italic">'date' | 'datetime' | 'time' | 'year' | 'year-month'</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Define la granularidad del selector.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4">Altura y tipografía del campo (36/40/48/56/64px).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600 italic">value</td>
                      <td class="px-6 py-4">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance italic">Valor en formato ISO (AAAA-MM-DD para fechas, HH:mm para horas).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">min / max</td>
                      <td class="px-6 py-4">string</td>
                      <td class="px-6 py-4 font-mono text-xs">-</td>
                      <td class="px-6 py-4">Límites temporales del calendario.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">disabledDates</td>
                      <td class="px-6 py-4">{' { days?: number[], dates?: string[] }'}</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance italic">Configuración de exclusión para días específicos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">format</td>
                      <td class="px-6 py-4 italic">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Máscara de visualización para el campo de texto.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default DateInputPage
